import React, { useState } from 'react'
import { 
  IonTabs, 
  IonTabBar, 
  IonTabButton, 
  IonIcon, 
  IonLabel, 
  IonRouterOutlet,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton
} from '@ionic/react'
import { Route, Redirect } from 'react-router-dom'
import { 
  homeOutline, 
  flagOutline, 
  calendarOutline, 
  chatbubbleOutline, 
  personOutline,
  colorPaletteOutline,
  settingsOutline,
  peopleOutline,
  analyticsOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'

import DashboardPage from '../pages/dashboard/DashboardPage'
import GoalsPage from '../pages/goals/GoalsPage'
import SessionsPage from '../pages/sessions/SessionsPage'
import ChatPage from '../pages/chat/ChatPage'
import ProfilePage from '../pages/profile/ProfilePage'
import AdminDashboard from '../pages/admin/AdminDashboard'
import CoacheeInsightsPage from '../pages/insights/CoacheeInsightsPage'
import LanguageSwitcher from './LanguageSwitcher'
import ThemeSwitcher from './ThemeSwitcher'

const MainTabs: React.FC = () => {
  const { t } = useTranslation()
  const { isAdmin, isCoach, user } = useAuthStore()
  const [isThemeSwitcherOpen, setIsThemeSwitcherOpen] = useState(false)

  return (
    <IonTabs>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('dashboard.welcome')}</IonTitle>
          <IonButtons slot="end">
            <div className="flex items-center gap-2 mr-2">
              <LanguageSwitcher size="small" />
              <IonButton 
                fill="clear" 
                size="small"
                onClick={() => setIsThemeSwitcherOpen(true)}
                title={t('theme.customize')}
              >
                <IonIcon icon={colorPaletteOutline} />
              </IonButton>
            </div>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonRouterOutlet>
        <Route exact path="/app/dashboard">
          <DashboardPage />
        </Route>
        <Route exact path="/app/goals">
          <GoalsPage />
        </Route>
        <Route exact path="/app/sessions">
          <SessionsPage />
        </Route>
        <Route exact path="/app/chat">
          <ChatPage />
        </Route>
        <Route exact path="/app/profile">
          <ProfilePage />
        </Route>
        <Route exact path="/app/admin">
          <AdminDashboard />
        </Route>
        <Route exact path="/app/insights">
          <CoacheeInsightsPage />
        </Route>

        <Route exact path="/app">
          {isAdmin() ? <Redirect to="/app/admin" /> : <Redirect to="/app/dashboard" />}
        </Route>
      </IonRouterOutlet>

      <IonTabBar slot="bottom">
        {isAdmin() ? (
          // Admin Navigation
          <>
            <IonTabButton tab="admin" href="/app/admin">
              <IonIcon icon={peopleOutline} />
              <IonLabel>Admin</IonLabel>
            </IonTabButton>
            
            <IonTabButton tab="dashboard" href="/app/dashboard">
              <IonIcon icon={homeOutline} />
              <IonLabel>{t('navigation.dashboard')}</IonLabel>
            </IonTabButton>
            
            <IonTabButton tab="profile" href="/app/profile">
              <IonIcon icon={personOutline} />
              <IonLabel>{t('navigation.profile')}</IonLabel>
            </IonTabButton>
          </>
        ) : (
          // Coach/Coachee Navigation
          <>
            <IonTabButton tab="dashboard" href="/app/dashboard">
              <IonIcon icon={homeOutline} />
              <IonLabel>{t('navigation.dashboard')}</IonLabel>
            </IonTabButton>
            
            <IonTabButton tab="goals" href="/app/goals">
              <IonIcon icon={flagOutline} />
              <IonLabel>{t('navigation.goals')}</IonLabel>
            </IonTabButton>
            
            <IonTabButton tab="sessions" href="/app/sessions">
              <IonIcon icon={calendarOutline} />
              <IonLabel>{t('navigation.sessions')}</IonLabel>
            </IonTabButton>
            
            {isCoach() && (
              <IonTabButton tab="insights" href="/app/insights">
                <IonIcon icon={analyticsOutline} />
                <IonLabel>Insights</IonLabel>
              </IonTabButton>
            )}
            
            <IonTabButton tab="chat" href="/app/chat">
              <IonIcon icon={chatbubbleOutline} />
              <IonLabel>{t('navigation.chat')}</IonLabel>
            </IonTabButton>
            
            <IonTabButton tab="profile" href="/app/profile">
              <IonIcon icon={personOutline} />
              <IonLabel>{t('navigation.profile')}</IonLabel>
            </IonTabButton>
          </>
        )}
      </IonTabBar>

      {/* Theme Switcher Modal */}
      <ThemeSwitcher 
        isOpen={isThemeSwitcherOpen} 
        onClose={() => setIsThemeSwitcherOpen(false)} 
      />
    </IonTabs>
  )
}

export default MainTabs 